/**
 * Modo do tema: `noite` (padrão) e `dia`.
 *
 * Portaria de show é de noite, e o tema escuro é o que o operador conhece. Mas
 * evento de dia existe — festival, jogo de tarde — e sob sol o tema escuro fica
 * ilegível. Quem alterna é o operador, na abertura do caixa: é o momento em que
 * ele já está olhando para o aparelho antes da fila começar.
 *
 * A escolha é do aparelho, não do servidor: dois postos do mesmo evento podem
 * estar um na sombra e outro no sol.
 */

import {useSyncExternalStore} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

export type Modo = 'noite' | 'dia';

const CHAVE = 'tmobi.validador.modo';

let atual: Modo = 'noite';
const ouvintes = new Set<() => void>();

function avisar(): void {
  ouvintes.forEach(ouvir => ouvir());
}

/** Lido uma vez na subida do app, antes da primeira tela. */
export async function carregarModo(): Promise<Modo> {
  try {
    const salvo = await AsyncStorage.getItem(CHAVE);
    atual = salvo === 'dia' ? 'dia' : 'noite';
  } catch {
    atual = 'noite';
  }
  avisar();
  return atual;
}

export function modoAtual(): Modo {
  return atual;
}

export function definirModo(modo: Modo): void {
  if (modo === atual) {
    return;
  }
  // A tela muda na hora; a gravação vai atrás. O operador não espera disco.
  atual = modo;
  avisar();
  AsyncStorage.setItem(CHAVE, modo).catch(e =>
    console.warn(`[modo] não gravou a preferência de tema: ${e}`),
  );
}

export function alternarModo(): void {
  definirModo(atual === 'noite' ? 'dia' : 'noite');
}

function assinar(ouvir: () => void): () => void {
  ouvintes.add(ouvir);
  return () => {
    ouvintes.delete(ouvir);
  };
}

export function useModo(): Modo {
  return useSyncExternalStore(assinar, modoAtual, modoAtual);
}
